import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CScoreChart from '../components/CScoreChart';
import Breadcrumb from '../components/Breadcrumb';
import Avatar from '../components/Avatar';
import HoverCard from '../components/HoverCard';
import ScoreBreakdown from '../components/ScoreBreakdown';
import DischargeBreakdown from '../components/DischargeBreakdown';
import { api } from '../lib/api';
import { useScoring } from '../lib/scoring';
import { SkeletonChart, SkeletonMetricCards, SkeletonBreadcrumb, SkeletonClinicianHeader } from '../components/Skeleton';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getScoreTextColor(score) {
  if (score >= 800) return '#1E6C30';
  if (score >= 740) return '#4d7c0f';
  if (score >= 670) return '#65782a';
  if (score >= 580) return '#DCA114';
  return '#B0200C';
}

export default function ClinicianDetail() {
  const { clinicianId } = useParams();
  const { weights } = useScoring();
  const [searchParams] = useSearchParams();
  const fromClinicId = searchParams.get('clinicId');
  const fromClinicName = searchParams.get('clinicName');
  const [clinician, setClinician] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load clinician and chart data
  useEffect(() => {
    Promise.all([
      api.clinicians.get(clinicianId),
      api.clinicians.chart(clinicianId),
    ])
      .then(([clinicianData, chart]) => {
        setClinician(clinicianData);
        setChartData(chart);
        // Default to the latest month/year
        if (chart.length > 0) {
          const latest = chart[chart.length - 1];
          setSelectedMonth(latest.month);
          setSelectedYear(latest.year);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicianId]);

  // Load month data when selection changes
  useEffect(() => {
    if (!selectedMonth || !selectedYear) return;
    api.clinicians.records(clinicianId, selectedMonth, selectedYear)
      .then(records => setMonthData(records[0] || null))
      .catch(console.error);
  }, [clinicianId, selectedMonth, selectedYear]);

  const years = [...new Set(chartData.map(d => d.year))].sort();

  if (loading || !clinician) return (
    <div>
      <SkeletonBreadcrumb />
      <SkeletonClinicianHeader />
      <SkeletonChart />
      <div className="mt-6"><SkeletonMetricCards /></div>
    </div>
  );

  return (
    <div>
      <Breadcrumb items={
        fromClinicId
          ? [
              { label: 'Home', to: '/' },
              { label: fromClinicName || 'Clinic', to: `/clinics/${fromClinicId}` },
              { label: clinician.full_name },
            ]
          : [
              { label: 'Home', to: '/' },
              { label: 'Clinicians', to: '/clinicians' },
              { label: clinician.full_name },
            ]
      } />

      {/* Clinician Header */}
      <div className="flex items-center gap-3 md:gap-4 mb-6">
        <Avatar src={clinician.image_url} name={clinician.full_name} size="lg" />
        <div className="min-w-0">
          <h1 className="text-xl md:text-[32px] font-semibold text-text truncate">{clinician.full_name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Clinician ID: {clinician.kipu_clinician_id}</p>
        </div>
      </div>

      {chartData.length > 0 && <CScoreChart data={chartData} />}

      {/* Month/Year Selectors */}
      <div className="flex gap-3 sm:gap-6 mt-6 mb-4">
        <div className="flex-1 sm:flex-none">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Month</label>
          <select
            value={selectedMonth || ''}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 sm:px-4 py-2.5 text-sm bg-white w-full sm:min-w-[160px] focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 sm:flex-none">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Year</label>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 sm:px-4 py-2.5 text-sm bg-white w-full sm:min-w-[160px] focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Metric Cards */}
      {monthData ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <div className="col-span-2 md:col-span-1 md:row-span-2 bg-white rounded-xl border-2 border-primary p-4 md:p-6 flex flex-col items-center justify-center">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-primary font-semibold text-sm">CScore</span>
              <div className="relative group">
                <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center cursor-help">
                  <span className="text-gray-400 text-xs">?</span>
                </div>
                <div className="absolute left-6 top-0 z-50 hidden group-hover:block bg-white rounded-xl shadow-lg border border-[#E6E6E6] p-5 w-[260px]">
                  <h4 className="text-base font-bold text-text mb-3">CScore Key</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#1E6C30' }} />
                      <span className="text-sm font-semibold text-text w-[100px]">EXCELLENT</span>
                      <span className="text-sm text-gray-600">800-850</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#4d7c0f' }} />
                      <span className="text-sm font-semibold text-text w-[100px]">VERY GOOD</span>
                      <span className="text-sm text-gray-600">740 - 799</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#DCA114' }} />
                      <span className="text-sm font-semibold text-text w-[100px]">GOOD</span>
                      <span className="text-sm text-gray-600">670 - 739</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#e67e22' }} />
                      <span className="text-sm font-semibold text-text w-[100px]">FAIR</span>
                      <span className="text-sm text-gray-600">580 - 669</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#B0200C' }} />
                      <span className="text-sm font-semibold text-text w-[100px]">POOR</span>
                      <span className="text-sm text-gray-600">0 - 579</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <HoverCard panel={<ScoreBreakdown breakdown={monthData} score={monthData.cscore} weights={weights} title="This month" />}>
              <span
                className="text-4xl md:text-5xl font-extrabold"
                style={{ color: getScoreTextColor(monthData.cscore || 0) }}
              >
                {monthData.cscore || 0}
              </span>
            </HoverCard>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 flex flex-col items-center justify-center">
            <span className="text-xs md:text-sm font-medium text-gray-600 mb-1">Caseload</span>
            <span className="text-2xl md:text-3xl font-extrabold text-text">{monthData.caseload || 0}</span>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 flex flex-col items-center justify-center">
            <span className="text-xs md:text-sm font-medium text-gray-600 mb-1">Discharges</span>
            <HoverCard panel={<DischargeBreakdown record={monthData} />}>
              <span className="text-2xl md:text-3xl font-extrabold text-text">{monthData.discharges || 0}</span>
            </HoverCard>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 flex flex-col items-center justify-center">
            <span className="text-xs md:text-sm font-medium text-gray-600 mb-1">Length of Stay</span>
            <span className="text-2xl md:text-3xl font-extrabold text-text">{monthData.length_of_stay || 0}</span>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 flex flex-col items-center justify-center">
            <span className="text-xs md:text-sm font-medium text-gray-600 mb-1">Attendance %</span>
            <span className="text-2xl md:text-3xl font-extrabold text-text">
              {monthData.attendance_pct ? (monthData.attendance_pct * 100).toFixed(2) + '%' : '0%'}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-gray-500 text-sm mt-4">No data for the selected month/year.</div>
      )}
    </div>
  );
}
